import { trackEvent } from "@utils/api/tracker";
import { fireFacebookCapi } from "@utils/api/FacebookCapiApi";
import { generateEventId } from "@utils/pixels/utils";
import { shouldAdjustELTV, adjustELTV, extractAdName } from "../eltvAdjustment";
import { registerHandler, Sha256Email } from "../handler";
import { getHealthyMindRevenueMetrics } from "../metrics";
import {
  allowCategories,
  consentFramework,
  LibCategories,
} from "src/utils/consent";
import { getCountryCode } from "src/utils/meristemContext";
import { optOutAllowsCookies } from "src/utils/consent/opt-out";

declare const fbq: any;

interface FacebookAdvancedMatchingParams {
  em?: string;
  fn?: string;
  ln?: string;
  email?: string;
  external_id?: string;
  ph?: string;
}

export interface FacebookPixelParameters {
  order_id?: string;
  external_id?: string;
  content_category?: string;
  content_ids?: string;
  content_type?: string;
  value?: number;
  currency?: string;
  product?: string;
}

const FB_EREV9_PIXEL = "1204952429523059";
const FB_ELTV13_PIXEL = "1557275494440599";
const FB_HM_PIXEL = "373833553789964";
const FB_FORBES_PIXEL = "830806628259122";

/**
 * Enables the advance matching feature for Facebook Pixel.
 */
function initAdvancedMatching(email: Sha256Email, userId: string) {
  const advancedMatchingParams: FacebookAdvancedMatchingParams = {
    ...(userId && { external_id: userId }),
  };

  // NOTE(Patrick): We already do this in the `init` handler but the logic
  // in ConversionTracker fires it twice so leaving it in.
  // Check to add LDU flag for CCPA opt out.
  if (shouldApplyLDUFlag()) {
    applyLDUFlag();
  }

  fbq("init", FB_EREV9_PIXEL, advancedMatchingParams);
  fbq("init", FB_ELTV13_PIXEL, advancedMatchingParams);
  fbq("init", FB_HM_PIXEL, advancedMatchingParams);
}

/**
 * Workaround for being able to add information as advanced matching latter in the funnel (if the purchase is successful)
 * This would have not been possible otherwise as the advanced matching parameters can only be added at the first call of 'init'
 */
function reinitForMatchingParameters() {
  if (allowCategories([LibCategories.targetingCookies])) {
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = `https://connect.facebook.net/signals/config/${FB_ELTV13_PIXEL}?v=2.9.48&r=stable`;
    script.id = "facebookEltv13PixelScript";

    const head = document.getElementsByTagName("head")[0];
    head.appendChild(script);
  }
}

export function shouldApplyLDUFlag() {
  return consentFramework() === "optOut" && !optOutAllowsCookies();
}

export function applyLDUFlag() {
  fbq("dataProcessingOptions", ["LDU"], 1, 1000);
}

export default function registerHandlers() {
  registerHandler("init", () => {
    // Check to add LDU flag for CCPA opt out.
    if (shouldApplyLDUFlag()) {
      applyLDUFlag();
    }

    fbq("init", FB_EREV9_PIXEL);
    fbq("init", FB_ELTV13_PIXEL);
    fbq("init", FB_HM_PIXEL);
    fbq("init", FB_FORBES_PIXEL);

    // We are generating 2 different custom IDs. One for ELTV13 Pixel, and one for the HM Pixel
    const eventIDEltv13Payload = { eventID: generateEventId() };
    const eventIDHMPayload = { eventID: generateEventId() };

    fbq("trackSingle", FB_ELTV13_PIXEL, "PageView", {}, eventIDEltv13Payload);

    fbq("trackSingle", FB_HM_PIXEL, "PageView", {}, eventIDHMPayload);
    fbq("trackSingle", FB_EREV9_PIXEL, "PageView");

    fireFacebookCapi(FB_ELTV13_PIXEL, "PageView", eventIDEltv13Payload);
    fireFacebookCapi(FB_HM_PIXEL, "PageView", eventIDHMPayload);

    fbq("trackSingle", FB_FORBES_PIXEL, "PageView");
  });

  registerHandler("viewContent", (params) => {
    fbq("trackSingle", FB_EREV9_PIXEL, "ViewContent", params);
    fbq("trackSingle", FB_ELTV13_PIXEL, "ViewContent", params);
  });

  registerHandler("lead", ({ emailHashes, userId, consentStatus }) => {
    // Enables the advance matching feature for Facebook Pixel.
    const eventID = generateEventId(userId);

    initAdvancedMatching(emailHashes.sha256, userId);

    const fbParams: JsonObject = {
      ...consentStatus,
    };

    fbq("trackSingle", FB_ELTV13_PIXEL, "Lead", fbParams, { eventID });
    fbq("trackSingle", FB_EREV9_PIXEL, "Lead", fbParams);

    fireFacebookCapi(FB_ELTV13_PIXEL, "Lead", {
      customProperties: {
        ...fbParams,
      },
      external_id: userId,
      eventID,
      email: emailHashes.sha256,
    });
  });

  registerHandler("HMLead", ({ emailHashes, userId, consentStatus }) => {
    const eventID = generateEventId(userId);

    initAdvancedMatching(emailHashes.sha256, userId);

    const fbParams: JsonObject = {
      product: "hm",
      ...consentStatus,
    };

    fbq("trackSingle", FB_ELTV13_PIXEL, "Lead", fbParams, { eventID });
    fbq("trackSingle", FB_EREV9_PIXEL, "Lead", fbParams);
    fbq("trackSingle", FB_HM_PIXEL, "Lead", fbParams, { eventID });

    fireFacebookCapi(FB_HM_PIXEL, "Lead", {
      ...fbParams,
      eventID,
      external_id: userId,
      email: emailHashes.sha256,
      customProperties: fbParams,
    });
  });

  registerHandler(
    "purchase",
    ({ emailHashes, order, plan, consentStatus, userId }) => {
      // refresh facebook script as early as possible
      reinitForMatchingParameters();

      const pixelProperties: FacebookPixelParameters = {
        ...(order.subscriptionId && { order_id: order.subscriptionId }),
        ...consentStatus,
        value: order.erev_9_months,
        currency: "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        external_id: userId,
      };

      // For FB algorithm optimization, we need to track both
      // Purchase and StartTrial events with identical properties.
      fbq("trackSingle", FB_EREV9_PIXEL, "StartTrial", pixelProperties);
      fbq("trackSingle", FB_ELTV13_PIXEL, "StartTrial", pixelProperties);

      const eltv13Value = shouldAdjustELTV()
        ? adjustELTV(order.eltv_13_months, getCountryCode())
        : order.eltv_13_months;

      if (eltv13Value !== order.eltv_13_months) {
        trackEvent("AdjustedELTV13", {
          originalValue: order.eltv_13_months,
          adjustedValue: eltv13Value,
          adName: extractAdName(),
        });
      }

      fbq("init", FB_ELTV13_PIXEL, {
        em: emailHashes.sha256,
      });
      fbq("trackSingle", FB_EREV9_PIXEL, "Purchase", pixelProperties);

      const eventID = generateEventId(userId);
      const fbParamsELTV13: FacebookPixelParameters = {
        ...pixelProperties,
        value: eltv13Value,
      };
      fbq("trackSingle", FB_ELTV13_PIXEL, "Purchase", fbParamsELTV13, {
        eventID,
      });
      fireFacebookCapi(FB_ELTV13_PIXEL, "Purchase", {
        ...fbParamsELTV13,
        ...consentStatus,
        eventID,
        email: emailHashes.sha256,
      });

      fbq("trackSingle", FB_FORBES_PIXEL, "Purchase");
    }
  );

  registerHandler(
    "trialFastForward",
    ({ discountOffered, userId, emailHashes }) => {
      const eventID = generateEventId(userId);

      const customProperties = {
        discountOffered,
      };
      const regularProperties = {
        external_id: userId,
        eventID,
      };
      const capiProperties = {
        email: emailHashes.sha256,
      };

      // Re-init the pixel
      reinitForMatchingParameters();
      fbq("init", FB_ELTV13_PIXEL, {
        em: emailHashes.sha256,
      });

      const pixelProperties: FacebookPixelParameters = {
        ...customProperties,
        ...regularProperties,
      };

      fbq("trackSingle", FB_ELTV13_PIXEL, "TrialFastForward", pixelProperties, {
        eventID,
      });
      fireFacebookCapi(FB_ELTV13_PIXEL, "TrialFastForward", {
        ...pixelProperties,
        ...capiProperties,
        customProperties,
      });
    }
  );

  registerHandler(
    "HMPurchase",
    ({ emailHashes, order, plan, consentStatus, userId }) => {
      const { erev9, eltv13 } = getHealthyMindRevenueMetrics(order);
      const customProperties = {
        ...consentStatus,
        product: "hm",
      };

      const pixelProperties: FacebookPixelParameters = {
        ...(order.subscriptionId && { order_id: order.subscriptionId }),
        ...customProperties,
        value: erev9,
        currency: "USD",
        content_ids: plan.braintree_id,
        content_type: "product",
        content_category: "recurring",
        external_id: userId,
      };

      const eventID = generateEventId(userId);
      const fbParamsELTV13: FacebookPixelParameters = {
        ...pixelProperties,
        value: eltv13,
      };

      // NOTE(di): For FB algorithm optimization, we need to track both Purchase and StartTrial events with identical properties.
      fbq("trackSingle", FB_EREV9_PIXEL, "StartTrial", pixelProperties);
      fbq("trackSingle", FB_ELTV13_PIXEL, "StartTrial", pixelProperties);
      fbq("trackSingle", FB_HM_PIXEL, "StartTrial", pixelProperties);

      fbq("trackSingle", FB_EREV9_PIXEL, "Purchase", pixelProperties);
      fbq("trackSingle", FB_ELTV13_PIXEL, "Purchase", fbParamsELTV13, {
        eventID,
      });

      fbq("trackSingle", FB_HM_PIXEL, "Purchase", pixelProperties, {
        eventID,
      });

      fireFacebookCapi(FB_HM_PIXEL, "Purchase", {
        ...pixelProperties,
        customProperties,
        eventID,
        email: emailHashes.sha256,
      });
    }
  );

  registerHandler("noomPremium", ({ signupEltv13, products }) => {
    fbq("trackSingle", FB_ELTV13_PIXEL, "PurchasedAddOns", {
      value: signupEltv13 + 40,
      products,
    });
  });

  registerHandler("noomSummit", ({ signupEltv13, products }) => {
    fbq("trackSingle", FB_ELTV13_PIXEL, "SummitUptake", {
      value: signupEltv13 + 100,
      products,
    });
  });
}
